import { useState, useEffect } from "react";
import { subirDatos, bajarDatos, eliminarDato } from "./syncSupabase";
import BotonMapaProceso from "./BotonMapaProceso";
import { authHeader } from './supabase';

const STORAGE_KEY = 'alqos_motor_costeo';

const storage = {
  get: (k, def = []) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } },
};

function fmt(n) { return Math.round(Number(n) || 0).toLocaleString('es-AR'); }

function calcularCosteo({ tiradaBase, demasiaPct, maquina, material }) {
  const tirada = Number(tiradaBase) || 0;
  const demasia = Number(demasiaPct) || 0;
  const tiradaConDemasia = tirada * (1 + demasia / 100);

  let costoMaterial = 0;
  let kgNecesarios = 0;
  if (material && Number(material.rendimientoUnidadPorKg) > 0) {
    const rendimiento = Number(material.rendimientoUnidadPorKg);
    const mermaProceso = Number(material.mermaProcesoPct) || 0;
    kgNecesarios = (tiradaConDemasia / rendimiento) * (1 + mermaProceso / 100);
    costoMaterial = kgNecesarios * (Number(material.costoKg) || 0);
  }

  let costoMaquina = 0;
  let horasProduccion = 0;
  if (maquina && Number(maquina.velocidad) > 0) {
    const velocidad = Number(maquina.velocidad);
    const mermaArranque = Number(maquina.mermaArranqueUnidades) || 0;
    const setupHoras = (Number(maquina.setupMinutos) || 0) / 60;
    horasProduccion = (tiradaConDemasia + mermaArranque) / velocidad;
    costoMaquina = (horasProduccion + setupHoras) * (Number(maquina.costoHora) || 0);
  }

  return {
    tiradaConDemasia,
    kgNecesarios: Math.round(kgNecesarios * 100) / 100,
    horasProduccion: Math.round(horasProduccion * 100) / 100,
    costoMaterial,
    costoMaquina,
    total: costoMaterial + costoMaquina,
  };
}

export default function MotorCosteo({ onVolver, oscuro, usuario }) {
  const [escenarios, setEscenarios] = useState(() => storage.get(STORAGE_KEY));
  const [specs, setSpecs] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [tab, setTab] = useState('nuevo');
  const [seleccionado, setSeleccionado] = useState(null);

  const [etId, setEtId] = useState('');
  const [maquinaId, setMaquinaId] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [tiradaBase, setTiradaBase] = useState('');
  const [demasiaPct, setDemasiaPct] = useState('');
  const [justificacionARIA, setJustificacionARIA] = useState('');
  const [cargandoARIA, setCargandoARIA] = useState(false);
  const [nombreEscenario, setNombreEscenario] = useState('');

  useEffect(() => {
    setSpecs(storage.get('et_specs'));
    setCentrosCosto(storage.get('alqos_centros_costo'));
    bajarDatos(STORAGE_KEY).then(huboDatos => {
      if (huboDatos) setEscenarios(storage.get(STORAGE_KEY));
    });
    const onSync = () => {
      setEscenarios(storage.get(STORAGE_KEY));
      setSpecs(storage.get('et_specs'));
      setCentrosCosto(storage.get('alqos_centros_costo'));
    };
    window.addEventListener('supabase-sync-complete', onSync);
    return () => window.removeEventListener('supabase-sync-complete', onSync);
  }, []);

  const maquinas = centrosCosto.filter(c => c.tipoRegistro === 'maquina');
  const materiales = centrosCosto.filter(c => c.tipoRegistro === 'material');

  const etSeleccionada = specs.find(s => String(s.id) === String(etId));
  const maquinaSeleccionada = maquinas.find(m => String(m.id) === String(maquinaId));
  const materialSeleccionado = materiales.find(m => String(m.id) === String(materialId));

  const aplicarET = (id) => {
    setEtId(id);
    const spec = specs.find(s => String(s.id) === String(id));
    if (spec?.tiradaValor) setTiradaBase(spec.tiradaValor);
  };

  const resultado = calcularCosteo({
    tiradaBase,
    demasiaPct,
    maquina: maquinaSeleccionada,
    material: materialSeleccionado,
  });

  const puedeCalcular = Number(tiradaBase) > 0 && (maquinaSeleccionada || materialSeleccionado);

  const sugerirDemasia = async () => {
    if (!Number(tiradaBase)) return alert('Cargá la tirada primero');
    setCargandoARIA(true);
    setJustificacionARIA('');
    const prompt = `Sos consultor experto en costeo de la industria gráfica argentina (offset, flexo, troquelado). Para el siguiente trabajo, sugerí un porcentaje de demasía (merma adicional de tirada por arranque, ajustes y calidad, más allá de la merma mecánica de la máquina) razonable según estándares de la industria:

Producto: ${etSeleccionada?.nombre || 'no especificado'}
Categoría: ${etSeleccionada?.categoria || 'no especificada'}
Sustrato: ${etSeleccionada?.sustrato || 'no especificado'}
Tirada base: ${tiradaBase} unidades
Troquel: ${etSeleccionada?.troquel ? 'sí' : 'no'}
Sistema de impresión: ${etSeleccionada?.sistema_impresion || maquinaSeleccionada?.nombre || 'no especificado'}

Respondé SOLO en este formato exacto, sin texto adicional:
PORCENTAJE: [número entero, ej: 8]
JUSTIFICACION: [una oración breve explicando el motivo]`;

    try {
      const res = await fetch('/api/aria', {
        method: 'POST',
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: 'claude-sonnet-4-5', max_tokens: 200, messages: [{ role: 'user', content: prompt }] }),
      });
      const data = await res.json();
      const texto = data.content?.[0]?.text || '';
      const matchPct = texto.match(/PORCENTAJE:\s*(\d+)/i);
      const matchJust = texto.match(/JUSTIFICACION:\s*(.+)/i);
      if (matchPct) setDemasiaPct(matchPct[1]);
      if (matchJust) setJustificacionARIA(matchJust[1].trim());
      if (!matchPct) setJustificacionARIA('No se pudo interpretar la respuesta de ARIA.');
    } catch (e) {
      setJustificacionARIA('Error de conexión con ARIA.');
    }
    setCargandoARIA(false);
  };

  const guardarEscenario = () => {
    if (!nombreEscenario.trim()) return alert('Ponele un nombre al escenario');
    if (!puedeCalcular) return alert('Cargá al menos tirada y un centro de costo (máquina o material)');

    const nuevo = {
      id: Math.floor(Date.now()),
      nombre: nombreEscenario,
      etId: etId || null,
      etNombre: etSeleccionada?.nombre || null,
      maquinaId: maquinaId || null,
      maquinaNombre: maquinaSeleccionada?.nombre || null,
      materialId: materialId || null,
      materialNombre: materialSeleccionado?.nombre || null,
      tiradaBase: Number(tiradaBase),
      demasiaPct: Number(demasiaPct) || 0,
      justificacionARIA,
      resultado,
      fechaCreacion: new Date().toLocaleDateString('es-AR'),
      creadoPor: usuario?.nombre || 'Sistema',
    };

    const lista = [nuevo, ...escenarios];
    setEscenarios(lista);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
    subirDatos(STORAGE_KEY);

    setNombreEscenario('');
    setTab('guardados');
  };

  const eliminarEscenario = async (id) => {
    if (!window.confirm('¿Eliminar este escenario de costeo?')) return;
    const lista = escenarios.filter(e => e.id !== id);
    setEscenarios(lista);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
    await eliminarDato(STORAGE_KEY, id);
    if (seleccionado?.id === id) setSeleccionado(null);
  };

  const inp = { width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };
  const lbl = { fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px', display: 'block' };
  const secTit = { fontSize: '11px', fontWeight: '700', color: '#1C3FAA', margin: '16px 0 10px', paddingBottom: '5px', borderBottom: '1px solid #E8E9F6', textTransform: 'uppercase', letterSpacing: '0.08em' };

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setTab(id)} style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '500', cursor: 'pointer', background: tab === id ? '#1C3FAA' : 'transparent', color: tab === id ? 'white' : '#6B7280' }}>{label}</button>
  );

  return (
    <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', background: '#E8E9F6', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={onVolver} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#6B7280' }}>← Menú</button>
            <span style={{ fontSize: '22px' }}>🧮</span>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>Motor de Costeo</h1>
            {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="motor_costeo" oscuro={oscuro} />}
          </div>
          <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginLeft: '32px' }}>Costo real de producción · ET + Centros de Costo + ARIA</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'white', padding: '4px', borderRadius: '10px', border: '1px solid #E5E7EB', width: 'fit-content', marginBottom: '20px' }}>
        <TabBtn id="nuevo" label="🧮 Nuevo costeo" />
        <TabBtn id="guardados" label={`📚 Escenarios guardados (${escenarios.length})`} />
      </div>

      {/* ── NUEVO COSTEO ── */}
      {tab === 'nuevo' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '14px' }}>

          <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #E5E7EB' }}>

            <div style={secTit}>📌 Especificación técnica (opcional)</div>
            <select style={inp} value={etId} onChange={e => aplicarET(e.target.value)}>
              <option value="">Sin ficha — cotización rápida</option>
              {specs.map(s => <option key={s.id} value={s.id}>{s.nombre} ({s.sku})</option>)}
            </select>

            <div style={secTit}>⚙️ Centro de costo — máquina</div>
            <select style={inp} value={maquinaId} onChange={e => setMaquinaId(e.target.value)}>
              <option value="">Sin máquina</option>
              {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre} ({m.velocidad || '—'} {m.unidadVelocidad || ''})</option>)}
            </select>

            <div style={secTit}>📦 Centro de costo — material</div>
            <select style={inp} value={materialId} onChange={e => setMaterialId(e.target.value)}>
              <option value="">Sin material</option>
              {materiales.map(m => <option key={m.id} value={m.id}>{m.nombre} ({m.rendimientoUnidadPorKg || '—'} {m.unidadRendimiento || ''})</option>)}
            </select>

            <div style={secTit}>📐 Tirada y demasía</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
              <div>
                <label style={lbl}>Tirada base (unidades)</label>
                <input style={inp} type="number" placeholder="Ej: 50000" value={tiradaBase} onChange={e => setTiradaBase(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Demasía (%)</label>
                <input style={inp} type="number" placeholder="Ej: 8" value={demasiaPct} onChange={e => setDemasiaPct(e.target.value)} />
              </div>
            </div>

            <button onClick={sugerirDemasia} disabled={cargandoARIA}
              style={{ width: '100%', padding: '10px', borderRadius: '10px', border: 'none', background: cargandoARIA ? '#9B7BFF' : '#7B5BF2', color: 'white', fontSize: '12px', fontWeight: '600', cursor: cargandoARIA ? 'default' : 'pointer', marginBottom: '8px' }}>
              {cargandoARIA ? '⏳ ARIA está pensando...' : '✨ Sugerir demasía con ARIA'}
            </button>

            {justificacionARIA && (
              <div style={{ background: '#F8F7FF', borderRadius: '10px', padding: '10px 12px', border: '1px solid #E0D9FF', fontSize: '12px', color: '#2F3E2E', marginBottom: '8px' }}>
                🤖 {justificacionARIA}
              </div>
            )}

            <div style={secTit}>💾 Guardar este escenario</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input style={{ ...inp, flex: 1 }} placeholder="Nombre del escenario (ej: Tirada corta)" value={nombreEscenario} onChange={e => setNombreEscenario(e.target.value)} />
              <button onClick={guardarEscenario} style={{ background: '#1C3FAA', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>Guardar</button>
            </div>
          </div>

          {/* Panel resultado */}
          <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #E5E7EB', alignSelf: 'start' }}>
            <div style={secTit}>Resultado</div>
            {!puedeCalcular ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#9CA3AF' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🧮</div>
                <div style={{ fontSize: '12px' }}>Cargá tirada y al menos un centro de costo para ver el cálculo</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>Tirada con demasía</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '14px' }}>{fmt(resultado.tiradaConDemasia)} unidades</div>

                {materialSeleccionado && (
                  <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '12px', marginBottom: '8px', border: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>📦 Material — {materialSeleccionado.nombre}</div>
                    <div style={{ fontSize: '11px', color: '#374151' }}>{resultado.kgNecesarios} kg necesarios</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#1C3FAA', marginTop: '4px' }}>${fmt(resultado.costoMaterial)}</div>
                  </div>
                )}

                {maquinaSeleccionada && (
                  <div style={{ background: '#F9FAFB', borderRadius: '10px', padding: '12px', marginBottom: '8px', border: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>⚙️ Máquina — {maquinaSeleccionada.nombre}</div>
                    <div style={{ fontSize: '11px', color: '#374151' }}>{resultado.horasProduccion} hs de producción</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#1C3FAA', marginTop: '4px' }}>${fmt(resultado.costoMaquina)}</div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px', background: '#1C3FAA', borderRadius: '10px', marginTop: '10px' }}>
                  <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>TOTAL</span>
                  <span style={{ color: 'white', fontWeight: '700', fontSize: '18px' }}>${fmt(resultado.total)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── ESCENARIOS GUARDADOS ── */}
      {tab === 'guardados' && (
        <div style={{ display: 'grid', gridTemplateColumns: seleccionado ? '1fr 380px' : '1fr', gap: '14px' }}>
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {escenarios.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#9CA3AF' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📚</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>Sin escenarios guardados</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    {['Nombre', 'ET', 'Tirada', 'Demasía', 'Total', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', fontSize: '11px', fontWeight: '600', color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {escenarios.map(esc => (
                    <tr key={esc.id} onClick={() => setSeleccionado(esc)} style={{ cursor: 'pointer', background: seleccionado?.id === esc.id ? '#EFF6FF' : 'white' }}
                      onMouseEnter={e => { if (seleccionado?.id !== esc.id) e.currentTarget.style.background = '#F9FAFB'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = seleccionado?.id === esc.id ? '#EFF6FF' : 'white'; }}>
                      <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: '600', fontSize: '13px', color: '#111827' }}>{esc.nombre}</td>
                      <td style={{ padding: '11px 14px', fontSize: '12px', color: '#374151', borderBottom: '1px solid #F3F4F6' }}>{esc.etNombre || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '12px', color: '#374151', borderBottom: '1px solid #F3F4F6' }}>{fmt(esc.tiradaBase)}</td>
                      <td style={{ padding: '11px 14px', fontSize: '12px', color: '#374151', borderBottom: '1px solid #F3F4F6' }}>{esc.demasiaPct}%</td>
                      <td style={{ padding: '11px 14px', fontSize: '13px', fontWeight: '700', color: '#1C3FAA', borderBottom: '1px solid #F3F4F6' }}>${fmt(esc.resultado?.total)}</td>
                      <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => eliminarEscenario(esc.id)} style={{ background: 'none', border: '1px solid #fee2e2', color: '#dc2626', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {seleccionado && (
            <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>{seleccionado.nombre}</div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{seleccionado.fechaCreacion} · {seleccionado.creadoPor}</div>
                </div>
                <button onClick={() => setSeleccionado(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
              </div>

              {seleccionado.etNombre && <div style={{ fontSize: '12px', color: '#374151', marginBottom: '6px' }}>📌 ET: {seleccionado.etNombre}</div>}
              {seleccionado.maquinaNombre && <div style={{ fontSize: '12px', color: '#374151', marginBottom: '6px' }}>⚙️ Máquina: {seleccionado.maquinaNombre}</div>}
              {seleccionado.materialNombre && <div style={{ fontSize: '12px', color: '#374151', marginBottom: '6px' }}>📦 Material: {seleccionado.materialNombre}</div>}
              <div style={{ fontSize: '12px', color: '#374151', marginBottom: '6px' }}>📐 Tirada base: {fmt(seleccionado.tiradaBase)} + {seleccionado.demasiaPct}% demasía = {fmt(seleccionado.resultado?.tiradaConDemasia)}</div>

              {seleccionado.justificacionARIA && (
                <div style={{ background: '#F8F7FF', borderRadius: '10px', padding: '10px 12px', border: '1px solid #E0D9FF', fontSize: '11px', color: '#2F3E2E', margin: '10px 0' }}>
                  🤖 {seleccionado.justificacionARIA}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px', background: '#1C3FAA', borderRadius: '10px', marginTop: '14px' }}>
                <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>TOTAL</span>
                <span style={{ color: 'white', fontWeight: '700', fontSize: '18px' }}>${fmt(seleccionado.resultado?.total)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
